<script lang="ts" module>
  /** One option in a SearchSelect — an id plus what to draw for it. */
  export type SearchOption = {
    id: string;
    name: string;
    avatarUrl?: string | null;
    subtitle?: string;
    /** Member id for the Avatar role-ring, when the option is a member. */
    ringId?: number | null;
  };
</script>

<script lang="ts">
  /**
   * A generic typeahead picker: a compact trigger that opens a debounced search over
   * whatever `search(q)` returns, and reports the chosen option. CONTROLLED — the parent
   * owns `value` (the selected id) and hears changes through `onchange`; a bare `value`
   * with no matching option is back-filled via `resolve`.
   *
   * Abstracted from the members picker so the same box serves any "search a list and pick
   * one" need — members, DM-inbox recipients, anything with a name and an avatar.
   */
  import Avatar from './Avatar.svelte';

  let {
    value = null,
    search,
    resolve,
    onchange,
    placeholder = 'Select…',
    searchPlaceholder = 'Search…',
    emptyText = 'No matches.',
    disabled = false,
  }: {
    value?: string | null;
    search: (q: string) => Promise<SearchOption[]>;
    resolve?: (id: string) => Promise<SearchOption | null>;
    onchange?: (opt: SearchOption | null) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyText?: string;
    disabled?: boolean;
  } = $props();

  let picked = $state<SearchOption | null>(null);
  let open = $state(false);
  let q = $state('');
  let results = $state<SearchOption[]>([]);
  let searching = $state(false);
  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  let inputEl = $state<HTMLInputElement | null>(null);
  let rootEl = $state<HTMLDivElement | null>(null);

  // Back-fill the chip when the parent sets `value` to something we don't hold.
  $effect(() => {
    const id = value;
    if (id == null) { picked = null; return; }
    if (picked?.id === id) return;
    if (!resolve) return;
    void (async () => {
      const opt = await resolve(id);
      if (value === id) picked = opt;
    })();
  });

  // Click-outside to close (self-contained, so this needs no host action).
  $effect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootEl && !rootEl.contains(e.target as Node)) open = false;
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  });

  async function runSearch(): Promise<void> {
    searching = true;
    try {
      results = await search(q.trim());
    } catch {
      results = [];
    } finally {
      searching = false;
    }
  }

  function onInput(e: Event): void {
    q = (e.target as HTMLInputElement).value;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(runSearch, 220);
  }

  function toggle(): void {
    if (disabled) return;
    open = !open;
    if (open) {
      q = '';
      void runSearch();
      queueMicrotask(() => inputEl?.focus());
    }
  }

  function choose(opt: SearchOption): void {
    picked = opt;
    open = false;
    onchange?.(opt);
  }

  function clear(): void {
    picked = null;
    open = false;
    onchange?.(null);
  }

  const triggerCls = $derived(
    'flex h-8 max-w-[15rem] items-center gap-1.5 rounded-lg border bg-base-100 px-2.5 text-sm transition-colors ' +
      (disabled ? 'opacity-50 cursor-not-allowed border-base-300 ' : '') +
      (picked
        ? 'border-primary text-base-content'
        : 'border-base-300 text-base-content/72 hover:border-base-content/25'),
  );
</script>

<div class="relative" bind:this={rootEl}>
  <button type="button" class={triggerCls} onclick={toggle} {disabled}
          aria-haspopup="listbox" aria-expanded={open}>
    {#if picked}
      <Avatar src={picked.avatarUrl ?? null} name={picked.name} size={20} />
      <span class="redact truncate font-medium">{picked.name}</span>
      <span role="button" tabindex="0" title="Clear"
            class="ml-0.5 -mr-0.5 rounded p-0.5 text-base-content/62 hover:bg-base-200 hover:text-base-content"
            onclick={(e) => { e.stopPropagation(); clear(); }}
            onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); clear(); } }}
      >✕</span>
    {:else}
      <svg class="h-4 w-4 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
      <span class="truncate">{placeholder}</span>
      <svg class="h-3.5 w-3.5 opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6" /></svg>
    {/if}
  </button>

  {#if open}
    <div class="absolute left-0 z-30 mt-1 w-72 rounded-xl border border-base-300 bg-base-100 p-1.5 shadow-xl">
      <input bind:this={inputEl} type="search" placeholder={searchPlaceholder}
             class="input input-sm input-bordered mb-1.5 w-full bg-base-100"
             value={q} oninput={onInput} aria-label={searchPlaceholder} />
      <div class="max-h-64 overflow-y-auto">
        {#if searching && results.length === 0}
          <div class="px-2 py-3 text-center text-xs text-base-content/62">Searching…</div>
        {:else if results.length === 0}
          <div class="px-2 py-3 text-center text-xs text-base-content/62">{emptyText}</div>
        {:else}
          {#each results as opt (opt.id)}
            <button type="button"
                    class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-base-200"
                    class:bg-base-200={picked?.id === opt.id}
                    onclick={() => choose(opt)}>
              <Avatar src={opt.avatarUrl ?? null} name={opt.name} size={26} memberId={opt.ringId ?? null} />
              <span class="min-w-0 flex-1">
                <span class="redact block truncate text-sm font-medium">{opt.name}</span>
                {#if opt.subtitle}
                  <span class="block truncate font-mono text-[0.65rem] text-base-content/65">{opt.subtitle}</span>
                {/if}
              </span>
            </button>
          {/each}
        {/if}
      </div>
    </div>
  {/if}
</div>
